import type React from 'react'
import type { PropsWithChildren } from 'react'
import { useId, useState } from 'react'

import { t } from 'i18next'
import { CheckIcon, ChevronDownIcon, XIcon, ChevronUpIcon } from 'lucide-react'
import type { FieldValues, UseControllerProps } from 'react-hook-form'

import { Render } from '@/shared/components/render-if'
import { Button, type ButtonProps } from '@/shared/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/components/ui/command'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover'
import { useIsMobile } from '@/shared/hooks'
import { cn } from '@/shared/lib'

type ValueMode = 'primitive' | 'object'

type IComboboxFieldProps<TOption, TFieldValues extends FieldValues = FieldValues> = PropsWithChildren<
  Omit<ButtonProps, 'onSelect'> &
    UseControllerProps<TFieldValues> & {
      label?: string
      options: TOption[]
      valueProperty?: keyof TOption
      labelProperty?: keyof TOption
      placeholderSearch?: string
      placeholder?: string
      valueMode?: ValueMode
      objectProperties?: Array<keyof TOption>
      hideClearButton?: boolean
      onClear?: () => void
      initialValue?: Pick<Partial<TOption>, keyof TOption>
      showSearch?: boolean
      disableFilter?: boolean
      onSelect?: (option: TOption) => void
      wrapperClassName?: string
    }
>

export const ComboboxField = <TOption extends Record<string, unknown>, TFieldValues extends FieldValues = FieldValues>({
  control,
  label,
  name,
  placeholder = 'Selecione',
  options,
  valueProperty = 'valueKey' as keyof TOption,
  labelProperty = 'labelKey' as keyof TOption,
  placeholderSearch,
  disabled,
  valueMode = 'primitive',
  objectProperties,
  hideClearButton = false,
  onClear,
  onSelect,
  initialValue,
  showSearch = true,
  disableFilter,
  className,
  wrapperClassName,
  ...props
}: IComboboxFieldProps<TOption, TFieldValues>) => {
  const id = useId()
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()

  const renderValue = (value: unknown): string => (value ? JSON.parse(JSON.stringify(value)) : '')

  const isValueEqual = (optionValue: unknown, fieldValue: unknown): boolean => {
    if (!fieldValue) return false

    if (typeof fieldValue === 'object' && fieldValue !== null && valueMode === 'object') {
      if (valueProperty in (fieldValue as Record<string, unknown>)) {
        return optionValue === (fieldValue as Record<string, unknown>)[valueProperty as string]
      }

      if (objectProperties?.length) {
        for (const prop of objectProperties) {
          if (
            prop in (fieldValue as Record<string, unknown>) &&
            optionValue === (fieldValue as Record<string, unknown>)[prop as string]
          ) {
            return true
          }
        }
      }

      return false
    }
    return optionValue === fieldValue
  }

  const findSelectedOption = (fieldValue: unknown): TOption | undefined =>
    options.find((opt) => isValueEqual(opt[valueProperty], fieldValue))

  const createObjectValue = (option: TOption): Record<string, unknown> =>
    objectProperties?.length
      ? Object.fromEntries(objectProperties.map((key) => [key, option[key]]))
      : { [valueProperty]: option[valueProperty] }

  const handleSelect = (option: TOption, onChange: (value: unknown) => void) => {
    const value = valueMode === 'primitive' ? option[valueProperty] : createObjectValue(option)
    onChange(value)
    setOpen(false)
  }

  const handleClear = (onChange: (value: unknown) => void, event: React.MouseEvent) => {
    event.stopPropagation()
    onClear?.()
    if (valueMode === 'primitive') return onChange('')
    onChange(initialValue)
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selectedOption = findSelectedOption(field.value)

        return (
          <FormItem className={cn('relative', wrapperClassName)}>
            <Render.If condition={!!label}>{() => <FormLabel aria-disabled={disabled}>{label}</FormLabel>}</Render.If>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant="outline"
                    id={id}
                    aria-expanded={open}
                    data-opened={String(open)}
                    disabled={disabled}
                    data-selected={String(!!selectedOption)}
                    className={cn(
                      'border-input truncate bg-neutral-50 px-3 text-base font-normal text-neutral-500 shadow-none hover:text-neutral-600',
                      'focus-visible:ring-primary outline-offset-0 outline-none',
                      'aria-[invalid=true]:border-destructive',
                      'data-[opened=true]:border-primary data-[opened=true]:border-1',
                      '[&:not(:disabled)[data-selected=true]]:bg-slate-100 [&:not(:disabled)[data-selected=true]]:text-gray-800',
                      'disabled:border-neutral-200 disabled:bg-neutral-100 disabled:text-neutral-500 disabled:opacity-100',
                      className,
                    )}
                    {...props}
                  >
                    <div className="flex w-full items-center justify-between gap-2 pr-0">
                      <span className="truncate">
                        {selectedOption ? renderValue(selectedOption[labelProperty]) : placeholder}
                      </span>
                      <div className="flex items-center gap-2">
                        <Render.If
                          condition={open}
                          fallback={<ChevronDownIcon size={16} className="text-muted-foreground/80 shrink-0" />}
                        >
                          {() => <ChevronUpIcon size={16} className="text-muted-foreground/80 shrink-0" />}
                        </Render.If>

                        {!hideClearButton && selectedOption && !disableFilter && (
                          <span
                            role="none"
                            onClick={(e) => handleClear(field.onChange, e)}
                            className={cn(
                              'flex h-6 w-6 items-center justify-center rounded-sm',
                              'text-muted-foreground/80 hover:bg-background',
                              'transition-[color,box-shadow] outline-none',
                            )}
                          >
                            <XIcon size={24} aria-hidden="true" />
                          </span>
                        )}
                      </div>
                    </div>
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent
                className="border-input w-full min-w-[var(--radix-popper-anchor-width)] p-0"
                align={isMobile ? 'center' : 'start'}
              >
                <Command
                  shouldFilter
                  filter={(_, search, keywords) =>
                    Number(keywords?.some((k) => k.toLowerCase().includes(search.toLowerCase())))
                  }
                >
                  <Render.If condition={showSearch}>
                    {() => <CommandInput disabled={disableFilter} placeholder={placeholderSearch} />}
                  </Render.If>
                  <CommandList>
                    <CommandEmpty>{t('common:sem_resultados')}</CommandEmpty>
                    <CommandGroup>
                      {options.map((option) => {
                        const optionValue = option[valueProperty]
                        const isSelected = isValueEqual(optionValue, field.value)
                        return (
                          <CommandItem
                            data-filled={isSelected}
                            key={String(optionValue)}
                            keywords={[String(option[labelProperty])]}
                            disabled={disableFilter}
                            onSelect={() => {
                              handleSelect(option, field.onChange)
                              onSelect?.(option)
                            }}
                            asChild
                          >
                            <span className="hover:bg-accent flex w-full cursor-pointer items-center justify-between px-2">
                              {renderValue(option[labelProperty])}
                              {isSelected && <CheckIcon size={16} />}
                            </span>
                          </CommandItem>
                        )
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
